
export const TEXT_SERVER = `// Your backend (Node.js 18+ with Express). Credentials stay on the server.
import crypto from 'crypto';
import express from 'express';

const app = express();
app.use(express.json());

const API_KEY = process.env.TODOAI_API_KEY;       // pk_YOUR_API_KEY
const SECRET_KEY = process.env.TODOAI_SECRET_KEY; // YOUR_SECRET_KEY
const DOMAIN = 'YOUR_DOMAIN';

app.post('/api/chat', async (req, res) => {
  const query = String(req.body.query || '').trim();
  if (!query) return res.status(400).json({ error: 'query is required' });

  // X-Signature = HMAC-SHA256 (hex) of the domain value
  const signature = crypto
    .createHmac('sha256', SECRET_KEY)
    .update(DOMAIN)
    .digest('hex');

  // multipart/form-data, as the endpoint does not accept JSON
  const form = new FormData();
  form.append('domain', DOMAIN);
  form.append('lang', String(req.body.lang || 'eng'));
  form.append('text_query', query);

  const upstream = await fetch('https://saas.todoai.ai/api/agents/generate-response/', {
    method: 'POST',
    headers: { 'X-API-Key': API_KEY, 'X-Signature': signature },
    body: form
  });
  const data = await upstream.json();

  if (!upstream.ok) {
    const message = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    return res.status(upstream.status).json({ error: message });
  }

  // Send the browser only what the page needs
  res.json({ response: data.response, documents: data.documents });
});

app.listen(3000);`;

export const TEXT_BROWSER = `// Browser code: calls YOUR backend only. No API key, secret or signature here.
const chatBox = document.getElementById('chat-box');
const userInput = document.getElementById('user-input');
const sendButton = document.getElementById('send-button');

// textContent shows text as-is, so user input and API text are never parsed as HTML
function addMessage(className, text) {
  const el = document.createElement('div');
  el.className = className;
  el.textContent = text;
  chatBox.appendChild(el);
  return el;
}

async function ask(query) {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: query, lang: 'eng' })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

sendButton.addEventListener('click', async () => {
  const query = userInput.value.trim();
  if (!query) return;

  addMessage('user-message', query);
  userInput.value = '';
  const reply = addMessage('ai-message', '...');

  try {
    const data = await ask(query);
    reply.textContent = data.response;
  } catch (err) {
    reply.textContent = 'Error: ' + err.message;
  }
});`;

export const AUDIO_SERVER = `// Your backend (Node.js 18+ with Express and multer). Credentials stay on the server.
import crypto from 'crypto';
import express from 'express';
import multer from 'multer';

const app = express();
const upload = multer({
  storage: multer.memoryStorage(),
  limits: { fileSize: 10 * 1024 * 1024 } // choose a limit that fits your use case
});

const API_KEY = process.env.TODOAI_API_KEY;       // pk_YOUR_API_KEY
const SECRET_KEY = process.env.TODOAI_SECRET_KEY; // YOUR_SECRET_KEY
const DOMAIN = 'YOUR_DOMAIN';

app.post('/api/chat-audio', upload.single('audio_file'), async (req, res) => {
  if (!req.file) return res.status(400).json({ error: 'audio_file is required' });

  const signature = crypto
    .createHmac('sha256', SECRET_KEY)
    .update(DOMAIN)
    .digest('hex');

  const form = new FormData();
  form.append('domain', DOMAIN);
  form.append('lang', String(req.body.lang || 'eng'));
  // Forward the recording unchanged: same bytes, same MIME type, same file name
  form.append(
    'audio_file',
    new Blob([req.file.buffer], { type: req.file.mimetype }),
    req.file.originalname
  );

  const upstream = await fetch('https://saas.todoai.ai/api/agents/generate-response/', {
    method: 'POST',
    headers: { 'X-API-Key': API_KEY, 'X-Signature': signature },
    body: form
  });
  const data = await upstream.json();

  if (!upstream.ok) {
    const message = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    return res.status(upstream.status).json({ error: message });
  }

  res.json({
    transcript: data.metadata && data.metadata.query,
    response: data.response
  });
});

app.listen(3000);`;

export const AUDIO_BROWSER = `// Browser code: records audio and sends it to YOUR backend. No credentials here.
const recordButton = document.getElementById('record-button');
const stopButton = document.getElementById('stop-button');
const chatBox = document.getElementById('chat-box');
let mediaRecorder;
let chunks = [];

function addMessage(className, text) {
  const el = document.createElement('div');
  el.className = className;
  el.textContent = text; // never innerHTML
  chatBox.appendChild(el);
  return el;
}

// The browser decides the recording format (Chrome: webm, Firefox: ogg,
// Safari: mp4). Keep it and use the matching file extension.
function extensionFor(mimeType) {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('wav')) return 'wav';
  throw new Error('Unsupported recording format: ' + mimeType);
}

recordButton.addEventListener('click', async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorder = new MediaRecorder(stream);
    chunks = [];

    mediaRecorder.ondataavailable = (event) => chunks.push(event.data);

    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach((track) => track.stop());

      // Use the real format. Labelling it 'audio/wav' would not convert it.
      const mimeType = mediaRecorder.mimeType;
      const blob = new Blob(chunks, { type: mimeType });
      const reply = addMessage('ai-message', '...');

      try {
        const form = new FormData();
        form.append('audio_file', blob, 'recording.' + extensionFor(mimeType));
        form.append('lang', 'eng');

        const res = await fetch('/api/chat-audio', { method: 'POST', body: form });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Request failed');

        addMessage('transcript', 'You said: ' + data.transcript);
        reply.textContent = data.response;
      } catch (err) {
        reply.textContent = 'Error: ' + err.message;
      }
    };

    mediaRecorder.start();
    recordButton.disabled = true;
    stopButton.disabled = false;
  } catch (err) {
    addMessage('error', 'Could not access the microphone. Please check permissions.');
  }
});

stopButton.addEventListener('click', () => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
    recordButton.disabled = false;
    stopButton.disabled = true;
  }
});`;
